<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Button from 'dashboard/components-next/button/Button.vue';

// M04: 'Hire your first agent' — the P1-2 importer behind a front door.
// Only the Maria fixture ships in Phase 1 (no template gallery, no slots).
const emit = defineEmits(['hired']);

const { t } = useI18n();
const store = useStore();

const source = ref('maria_fixture');
const handle = ref('');
const name = ref('');

const uiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const isHiring = computed(() => uiFlags.value.creatingItem);

const sources = [
  { value: 'maria_fixture', labelKey: 'AI_AGENTS.HIRE.SOURCES.MARIA_FIXTURE' },
];

const isSubmittable = computed(() => source.value && handle.value.trim());

const hire = async () => {
  if (!isSubmittable.value) return;
  try {
    const brain = await store.dispatch('aiAgentBrains/hire', {
      source: source.value,
      handle: handle.value.trim(),
      name: name.value.trim() || undefined,
    });
    useAlert(t('AI_AGENTS.HIRE.SUCCESS'));
    emit('hired', brain);
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t('AI_AGENTS.HIRE.ERROR'));
  }
};
</script>

<template>
  <form
    class="flex flex-col gap-4 w-full max-w-md mx-auto"
    @submit.prevent="hire"
  >
    <div class="flex flex-col gap-1 text-center">
      <h2 class="text-xl font-medium text-n-slate-12">
        {{ t('AI_AGENTS.HIRE.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.HIRE.DESCRIPTION') }}
      </p>
    </div>

    <label class="flex flex-col gap-1 text-sm text-n-slate-12">
      {{ t('AI_AGENTS.HIRE.SOURCE_LABEL') }}
      <select
        v-model="source"
        class="px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
      >
        <option
          v-for="option in sources"
          :key="option.value"
          :value="option.value"
        >
          {{ t(option.labelKey) }}
        </option>
      </select>
    </label>

    <label class="flex flex-col gap-1 text-sm text-n-slate-12">
      {{ t('AI_AGENTS.HIRE.HANDLE_LABEL') }}
      <input
        v-model="handle"
        type="text"
        :placeholder="t('AI_AGENTS.HIRE.HANDLE_PLACEHOLDER')"
        class="px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
      />
    </label>

    <label class="flex flex-col gap-1 text-sm text-n-slate-12">
      {{ t('AI_AGENTS.HIRE.NAME_LABEL') }}
      <input
        v-model="name"
        type="text"
        :placeholder="t('AI_AGENTS.HIRE.NAME_PLACEHOLDER')"
        class="px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
      />
    </label>

    <Button
      type="submit"
      :label="t('AI_AGENTS.HIRE.SUBMIT')"
      :is-loading="isHiring"
      :disabled="!isSubmittable || isHiring"
    />
  </form>
</template>
